(() => {
  class AzulejoRoomViewer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.background = this.querySelector('[data-room-viewer-background]');
      this.scene = this.querySelector('[data-room-viewer-scene]');
      this.liveRegion = this.querySelector('[data-room-viewer-live]');
      this.buttons = Array.from(this.querySelectorAll('[data-room-viewer-button]'));
      this.productId = this.dataset.productId || '';
      this.productTitle = this.dataset.productTitle || '';
      this.switchToken = 0;
      this.engagementTracked = false;

      if (!this.background || !this.scene || !this.buttons.length) return;

      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKeydown);

      const activeButton = this.buttons.find((button) => button.getAttribute('aria-pressed') === 'true') || this.buttons[0];
      this.setActiveState(activeButton);
      this.observeVisibility();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeydown);
      this.visibilityObserver?.disconnect();
    }

    handleClick(event) {
      const button = event.target.closest('[data-room-viewer-button]');
      if (!button || !this.contains(button)) return;
      this.activateRoom(button, { track: true });
    }

    handleKeydown(event) {
      const current = event.target.closest('[data-room-viewer-button]');
      if (!current || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

      event.preventDefault();
      const currentIndex = this.buttons.indexOf(current);
      let nextIndex = currentIndex;

      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.buttons.length) % this.buttons.length;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.buttons.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.buttons.length - 1;

      const nextButton = this.buttons[nextIndex];
      nextButton.focus();
      this.activateRoom(nextButton, { track: true });
    }

    setActiveState(activeButton) {
      this.buttons.forEach((button) => {
        const isActive = button === activeButton;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    async activateRoom(button, { track = false } = {}) {
      if (!button) return;

      const source = button.dataset.roomImage || '';
      const roomLabel = button.dataset.roomLabel || button.getAttribute('aria-label') || '';
      const isAlreadyActive = button.getAttribute('aria-pressed') === 'true' && this.background.currentSrc === source;

      if (track) this.trackEngagementOnce();

      this.setActiveState(button);
      this.scene.setAttribute('aria-label', [this.productTitle, roomLabel].filter(Boolean).join(' – '));
      if (this.liveRegion) this.liveRegion.textContent = roomLabel ? `${roomLabel} ausgewählt` : '';

      if (!source || isAlreadyActive) return;

      const token = ++this.switchToken;
      this.classList.add('is-switching');

      try {
        const preload = new Image();
        preload.src = source;
        if (typeof preload.decode === 'function') await preload.decode();
        else await new Promise((resolve) => {
          preload.onload = resolve;
          preload.onerror = resolve;
        });
      } catch (error) {
        // Auch bei einem Decode-Fehler kann der Browser das Bild regulär laden.
      }

      if (token !== this.switchToken) return;
      this.background.src = source;

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => this.classList.remove('is-switching'));
      });

    }

    preloadRoomImages() {
      const run = () => {
        this.buttons.forEach((button) => {
          const source = button.dataset.roomImage;
          if (!source || source === this.background.currentSrc || source === this.background.src) return;
          const image = new Image();
          image.src = source;
        });
      };

      if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 1800 });
      else window.setTimeout(run, 650);
    }

    observeVisibility() {
      if (!('IntersectionObserver' in window)) {
        window.setTimeout(() => this.preloadRoomImages(), 900);
        return;
      }

      this.visibilityObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        this.preloadRoomImages();
        this.visibilityObserver.disconnect();
      }, { threshold: 0.35 });

      this.visibilityObserver.observe(this);
    }

    trackEngagementOnce() {
      if (this.engagementTracked) return;
      this.engagementTracked = true;
      this.publish('Raumvisualisierung genutzt');
    }

    publish(metricName) {
      try {
        if (!window.Shopify?.analytics || typeof window.Shopify.analytics.publish !== 'function') return;

        window.Shopify.analytics.publish('azullado:room_viewer', {
          event_action: 'Produkt-Raumansicht',
          metric_name: metricName,
          source: 'product-room-viewer',
          product_id: this.productId,
          product_title: this.productTitle
        });
      } catch (error) {
        // Tracking darf die Bedienung niemals blockieren.
      }
    }
  }

  if (!customElements.get('azulejo-room-viewer')) {
    customElements.define('azulejo-room-viewer', AzulejoRoomViewer);
  }
})();
